import dayjs from 'dayjs';
import { getSupabaseClient } from '@/core/supabase/client.js';
import type { SaleEntity, SaleStats } from '@/features/sale/domain/entities/SaleEntity.js';
import type { SaleItemEntity, SaleReturnItemEntity } from '@/features/sale/domain/entities/SaleItemEntity.js';
import type { CreateSaleInput, UpdateSaleInput, ListSalesParams, ListSalesResult, SaleStatsParams, ReturnSaleInput } from '@/features/sale/domain/repositories/ISaleRepository.js';
import { parseSaleReturnItems } from '@/features/sale/data/dtos/SaleReturnDto.js';

const SALE_SELECT = `
  id,
  code,
  project_id,
  location_id,
  session_id,
  customer_id,
  total_amount,
  status,
  applied_promotions,
  bill_photos,
  notes,
  metadatas,
  created_at,
  updated_at,
  created_by,
  updated_by,
  customer:contacts(id, full_name, phone, id_number),
  location:locations(id, name, code, full_address_cache, address_line),
  session:sessions(id, started_at, ended_at, scheduled_start_at, scheduled_end_at),
  creator:profiles!sales_created_by_fk(id, first_name, last_name, email, avatar_url)
`;

const SALE_ITEM_SELECT = `
  id,
  sale_id,
  product_id,
  qty,
  price,
  type,
  source_batch_id,
  stock_transaction_item_id,
  stock_item:stock_transaction_items!sale_items_stock_transaction_item_id_fkey(
    batch_code_link:stock_transaction_batch_codes(
      batch_code_id,
      batch_metadata:stock_batch_codes(id,batch_code,mfg_date,exp_date)
    )
  ),
  created_at,
  product:stock_assets(id, name, sku, unit, unit_price, photos)
`;

export class SaleRemoteDataSource {
  private get supabase() {
    return getSupabaseClient() as any;
  }

  private parseBillPhotos(raw: unknown): string[] {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map((item) => String(item));
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map((item) => String(item));
      } catch {
        return [raw];
      }
    }
    return [];
  }

  private mapToSaleEntity(raw: any): SaleEntity {
    return {
      id: raw.id,
      code: raw.code || undefined,
      projectId: raw.project_id,
      locationId: raw.location_id,
      sessionId: raw.session_id,
      customerId: raw.customer_id,
      totalAmount: Number(raw.total_amount || 0),
      status: raw.status,
      appliedPromotions: Array.isArray(raw.applied_promotions) ? raw.applied_promotions : [],
      billPhotos: this.parseBillPhotos(raw.bill_photos),
      notes: raw.notes,
      metadatas: raw.metadatas && typeof raw.metadatas === 'object' ? raw.metadatas : {},
      createdAt: raw.created_at,
      updatedAt: raw.updated_at,
      createdBy: raw.created_by,
      updatedBy: raw.updated_by,
      customer: raw.customer
        ? {
            id: raw.customer.id,
            fullName: raw.customer.full_name,
            phone: raw.customer.phone,
            idNumber: raw.customer.id_number,
          }
        : undefined,
      location: raw.location
        ? {
            id: raw.location.id,
            name: raw.location.name,
            code: raw.location.code,
            address: raw.location.full_address_cache || raw.location.address_line || undefined,
          }
        : undefined,
      session: raw.session
        ? {
            id: raw.session.id,
            startedAt: raw.session.started_at,
            endedAt: raw.session.ended_at,
            scheduledStartAt: raw.session.scheduled_start_at,
            scheduledEndAt: raw.session.scheduled_end_at,
            name: raw.session.scheduled_start_at && raw.session.scheduled_end_at
              ? `${dayjs(raw.session.scheduled_start_at).format('HH:mm')} - ${dayjs(raw.session.scheduled_end_at).format('HH:mm')}`
              : raw.session.started_at
              ? `Ca ${dayjs(raw.session.started_at).format('DD/MM HH:mm')}`
              : undefined,
          }
        : undefined,
      creator: raw.creator
        ? {
            id: raw.creator.id,
            fullName: [raw.creator.first_name, raw.creator.last_name].filter(Boolean).join(' ') || raw.creator.email || undefined,
            email: raw.creator.email,
            avatarUrl: raw.creator.avatar_url,
          }
        : undefined,
    };
  }

  private mapToSaleItemEntity(raw: any): SaleItemEntity {
    const stockItem = Array.isArray(raw.stock_item) ? raw.stock_item[0] : raw.stock_item;
    const link = Array.isArray(stockItem?.batch_code_link) ? stockItem.batch_code_link[0] : stockItem?.batch_code_link;
    const metadata = Array.isArray(link?.batch_metadata) ? link.batch_metadata[0] : link?.batch_metadata;
    return {
      id: raw.id,
      saleId: raw.sale_id,
      productId: raw.product_id,
      qty: Number(raw.qty || 0),
      price: Number(raw.price || 0),
      type: raw.type,
      createdAt: raw.created_at,
      sourceBatchId: raw.source_batch_id,
      stockTransactionItemId: raw.stock_transaction_item_id,
      batchCodeId: link?.batch_code_id,
      batchCode: metadata?.batch_code,
      mfgDate: metadata?.mfg_date,
      expDate: metadata?.exp_date,
      product: raw.product
        ? {
            id: raw.product.id,
            name: raw.product.name,
            sku: raw.product.sku,
            unit: raw.product.unit,
            unitPrice: raw.product.unit_price ? Number(raw.product.unit_price) : undefined,
            imageUrl: Array.isArray(raw.product.photos) && raw.product.photos.length > 0
              ? raw.product.photos[0]
              : undefined,
          }
        : undefined,
    };
  }

  async listSales(params: ListSalesParams): Promise<ListSalesResult> {
    const page = params.page && params.page > 0 ? params.page : 1;
    const pageSize = params.pageSize && params.pageSize > 0 ? params.pageSize : 20;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const query = params.search?.trim();

    if (query) {
      // Let PostgREST paginate/count the fully filtered RPC result, not an internal page.
      const { data, count, error } = await this.supabase
        .rpc('search_sales', {
          p_project_id: params.projectId,
          p_location_id: params.locationId || null,
          p_session_id: params.sessionId || null,
          p_customer_id: params.customerId || null,
          p_status: params.status || null,
          p_promotion_id: params.promotionId || null,
          p_from_date: params.fromDate || null,
          p_to_date: params.toDate || null,
          search_query: query,
          row_limit: null,
          row_offset: 0,
        }, { count: 'exact' })
        .select(SALE_SELECT)
        .range(from, to);

      if (error) throw error;
      return {
        items: (data || []).map((row: unknown) => this.mapToSaleEntity(row)),
        total: count ?? 0,
      };
    }

    let q = this.supabase
      .from('sales')
      .select(SALE_SELECT, { count: 'exact' })
      .eq('project_id', params.projectId);

    if (params.locationId) q = q.eq('location_id', params.locationId);
    if (params.sessionId) q = q.eq('session_id', params.sessionId);
    if (params.customerId) q = q.eq('customer_id', params.customerId);
    if (params.status) q = q.eq('status', params.status);
    if (params.promotionId) q = q.contains('applied_promotions', JSON.stringify([{ promotionId: params.promotionId }]));
    if (params.fromDate) q = q.gte('created_at', params.fromDate);
    if (params.toDate) q = q.lte('created_at', params.toDate);

    q = q.order('created_at', { ascending: false }).order('id', { ascending: false }).range(from, to);

    const { data, count, error } = await q;
    if (error) throw error;

    return {
      items: (data || []).map((row: any) => this.mapToSaleEntity(row)),
      total: count ?? 0,
    };
  }

  async getSaleDetail(saleId: string): Promise<{ sale: SaleEntity; items: SaleItemEntity[]; returnItems: SaleReturnItemEntity[] }> {
    const { data: saleData, error: saleError } = await this.supabase
      .from('sales')
      .select(SALE_SELECT)
      .eq('id', saleId)
      .single();

    if (saleError) throw saleError;

    const { data: itemsData, error: itemsError } = await this.supabase
      .from('sale_items')
      .select(SALE_ITEM_SELECT)
      .eq('sale_id', saleId)
      .order('created_at', { ascending: true });

    if (itemsError) throw itemsError;

    const { data: returnData, error: returnError } = await this.supabase.rpc('get_sale_return_items', { p_sale_id: saleId });
    if (returnError) throw returnError;

    return {
      sale: this.mapToSaleEntity(saleData),
      items: (itemsData || []).map((item: any) => this.mapToSaleItemEntity(item)),
      returnItems: parseSaleReturnItems(returnData),
    };
  }

  async createSale(input: CreateSaleInput): Promise<string> {
    const { data, error } = await this.supabase.rpc('create_sale_transaction', {
      p_project_id: input.projectId,
      p_location_id: input.locationId,
      p_session_id: input.sessionId || null,
      p_customer_id: input.customerId || null,
      p_total_amount: input.totalAmount,
      p_applied_promotions: input.appliedPromotions || [],
      p_items: input.items.map((item) => ({
        product_id: item.productId,
        qty: item.qty,
        price: item.price,
        type: item.type,
        ...(item.sourceBatchId ? { source_batch_id: item.sourceBatchId } : {}),
      })),
      p_bill_photos: JSON.stringify(input.billPhotos || []),
      p_notes: input.notes || null,
      p_metadatas: input.metadatas || {},
    });

    if (error) throw error;
    return String(data);
  }

  async updateSale(saleId: string, input: UpdateSaleInput): Promise<boolean> {
    const updatePayload: Record<string, unknown> = {};

    if (input.customerId !== undefined) updatePayload.customer_id = input.customerId;
    if (input.notes !== undefined) updatePayload.notes = input.notes;
    if (input.billPhotos !== undefined) updatePayload.bill_photos = JSON.stringify(input.billPhotos);
    if (input.metadatas !== undefined) updatePayload.metadatas = input.metadatas;

    const { data, error } = await this.supabase.rpc('update_sale_details', {
      p_sale_id: saleId,
      p_input: updatePayload,
    });

    if (error) throw error;
    return data === true;
  }

  async deleteSale(input: ReturnSaleInput): Promise<boolean> {
    const { data, error } = await this.supabase.rpc('delete_sale_transaction', {
      p_sale_id: input.saleId,
      p_return_items: input.returnItems.map((row) => ({
        return_item_id: row.returnItemId, batch_code: row.batchCode, mfg_date: row.mfgDate, exp_date: row.expDate,
      })),
    });

    if (error) throw error;
    return data === true;
  }

  async cancelSale(input: ReturnSaleInput): Promise<boolean> {
    const { data, error } = await this.supabase.rpc('cancel_sale_transaction', {
      p_sale_id: input.saleId,
      p_return_items: input.returnItems.map((row) => ({
        return_item_id: row.returnItemId, batch_code: row.batchCode, mfg_date: row.mfgDate, exp_date: row.expDate,
      })),
    });

    if (error) throw error;
    return data === true;
  }

  async getSaleStats(params: SaleStatsParams): Promise<SaleStats> {
    const { data, error } = await this.supabase.rpc('get_sale_stats', {
      p_project_id: params.projectId,
      p_location_id: params.locationId || null,
      p_session_id: params.sessionId || null,
      p_customer_id: params.customerId || null,
      p_status: params.status || null,
      p_promotion_id: params.promotionId || null,
      p_from_date: params.fromDate || null,
      p_to_date: params.toDate || null,
      search_query: params.search?.trim() || '',
    });
    if (error) throw error;

    return {
      totalRevenue: Number(data.totalRevenue),
      totalCount: Number(data.totalCount),
      completedCount: Number(data.completedCount),
      cancelledCount: Number(data.cancelledCount),
      averageOrderValue: Number(data.averageOrderValue),
    };
  }
}
